/**
 * Each valuation method as a band: the 10-90 range from the forecast's measured error and what
 * the filing does not vouch for, the source that drives it most, and the method's own spread on
 * the reference set beside it (not inside it). A figure confirmed with "Looks right" narrows the
 * band on the next version. Fetched per model version: a few seconds of sampling on the server.
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../../../store/auth';
import { formatMoney } from '../../../lib/currency';

export interface MethodBand {
  method: string;
  point: number | null;
  p10: number | null;
  p50: number | null;
  p90: number | null;
  drivers: { name: string; share: number; detail: string }[];
  referenceSpread: number | null;
  restsOn: string[];
}

export interface Bands {
  samples: number;
  methods: MethodBand[];
  sources: string[];
  notes: string[];
}

const NAMES: Record<string, string> = {
  'dcf (alone)': 'DCF',
  'exit multiple': 'Exit multiple',
  ev_ebitda: 'EV/EBITDA',
  ev_revenue: 'EV/Revenue',
  pe: 'P/E',
  pb: 'P/B',
  residual_income: 'Residual income',
  ddm: 'Dividend discount',
  liquidation: 'Liquidation',
};

export function BandsTable({ bands, currency }: { bands: Bands; currency: string }) {
  const rows = bands.methods.filter((m) => m.method in NAMES && m.point !== null);
  return (
    <>
      <table className="mt-3 w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1 font-medium">Method</th>
            <th className="py-1 text-right font-medium">Low (10%)</th>
            <th className="py-1 text-right font-medium">Point</th>
            <th className="py-1 text-right font-medium">High (90%)</th>
            <th className="py-1 pl-3 font-medium">Driven by</th>
            <th className="py-1 text-right font-medium">Usual distance from market</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((m) => {
            const top = m.drivers[0];
            return (
              <tr key={m.method}>
                <td className="py-1 text-gray-800">{NAMES[m.method]}</td>
                <td className="py-1 text-right font-mono">{formatMoney(m.p10 ?? 0, currency)}</td>
                <td className="py-1 text-right font-mono">{formatMoney(m.point ?? 0, currency)}</td>
                <td className="py-1 text-right font-mono">{formatMoney(m.p90 ?? 0, currency)}</td>
                <td className="py-1 pl-3 text-gray-600">
                  {top ? `${top.name} (${Math.round(top.share * 100)}%)${top.detail ? ` - ${top.detail}` : ''}` : 'nothing sampled'}
                </td>
                <td className="py-1 text-right text-gray-500">
                  {m.referenceSpread === null ? '-' : `${Math.exp(m.referenceSpread).toFixed(2)}x`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {bands.notes.map((n) => (
        <p key={n} className="mt-2 text-xs text-amber-700">
          {n}
        </p>
      ))}
    </>
  );
}

export function BandsPanel({ modelId, version, currency }: { modelId: string; version: number; currency: string }) {
  const [bands, setBands] = useState<Bands | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    setBands(null);
    setError(false);
    const token = useAuthStore.getState().token;
    fetch(`/api/v2/models/${modelId}/uncertainty`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((b: Bands) => live && setBands(b))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [modelId, version]);
  return (
    <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4" data-testid="bands-panel">
      <h3 className="text-sm font-semibold text-gray-900">How sure each value is</h3>
      <p className="mt-0.5 text-xs text-gray-600">
        The 10-90% range from the forecast's measured error and the figures the filing does not vouch
        for. Confirming a figure narrows it.
      </p>
      {error && <p className="mt-2 text-xs text-gray-500">The ranges could not be computed.</p>}
      {!bands && !error && <p className="mt-2 text-xs text-gray-500">Computing ranges…</p>}
      {bands && <BandsTable bands={bands} currency={currency} />}
    </section>
  );
}
